/**
 * PATH: packages/ui/src/kanban/components/settings-home/SettingsHomeShell.tsx
 * FILE_ID: UI-KANBAN-SETTINGS-SHELL-001
 * PURPOSE: Settings chrome — centered popup on desktop, full screen on a phone.
 * ROLE IN ARCHITECTURE: Presentational frame around settings-home page bodies.
 * MAIN EXPORTS: SettingsHomeShell.
 * NON-RESPONSIBILITIES: Does not persist prefs or own page bodies.
 * NOTES FOR FUTURE AI: Desktop nav sits on the right. Phone must not keep a fixed rail.
 */

import { ArrowLeft, Settings as SettingsIcon, X } from 'lucide-react'
import type { ReactNode } from 'react'

import { SettingsNav } from './chrome'
import { pageMeta, type SettingsPage } from './nav'
import type { SettingsViewport } from './useSettingsViewport'

export function SettingsHomeShell({
  viewport,
  page,
  onPage,
  onClose,
  phoneShowingNav,
  onPhoneBack,
  boardName,
  navGroups,
  status,
  toolbar,
  mode = 'overlay',
  children,
}: {
  viewport: SettingsViewport
  page: SettingsPage
  onPage: (page: SettingsPage) => void
  onClose: () => void
  phoneShowingNav: boolean
  onPhoneBack: () => void
  boardName?: string
  navGroups?: Array<'board' | 'kanban'>
  status?: ReactNode
  toolbar?: ReactNode
  mode?: 'overlay' | 'embedded'
  children: ReactNode
}) {
  const meta = pageMeta(page)
  const phone = viewport === 'phone'
  const tablet = viewport === 'tablet'
  const showRail = !phone
  const showBody = !phone || !phoneShowingNav

  return (
    <aside
      data-testid="settings-home-shell"
      data-viewport={viewport}
      className={[
        'flex flex-col bg-white dark:bg-zinc-950',
        mode === 'overlay'
          ? phone
            ? 'absolute inset-0 h-full w-full'
            : 'absolute left-1/2 top-1/2 h-[min(86vh,780px)] w-[min(92vw,1080px)] -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-xl border border-neutral-200 shadow-2xl dark:border-zinc-800'
          : 'relative h-full min-h-[28rem] w-full',
      ].join(' ')}
    >
      <header
        className={[
          'flex items-center justify-between border-b border-neutral-200 px-4 py-3 dark:border-zinc-800',
          phone ? 'sticky top-0 z-10 bg-white dark:bg-zinc-950' : '',
        ].join(' ')}
      >
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold text-neutral-800 dark:text-neutral-100">
            {phone && !phoneShowingNav ? (
              <button
                type="button"
                data-testid="settings-phone-back"
                aria-label="Back"
                onClick={onPhoneBack}
                className="rounded p-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-zinc-800"
              >
                <ArrowLeft className="h-4 w-4" />
              </button>
            ) : (
              <SettingsIcon className="h-4 w-4" />
            )}
            Settings
            {boardName ? (
              <span className="max-w-[12rem] truncate text-xs font-normal text-neutral-400">
                — {boardName}
              </span>
            ) : null}
          </div>
          <p className="mt-0.5 text-[11px] text-neutral-400">
            {meta.title}. {meta.hint}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close settings"
          data-testid="settings-close"
          className="rounded p-1 text-neutral-500 hover:bg-neutral-100 dark:hover:bg-zinc-800"
        >
          <X className="h-4 w-4" />
        </button>
      </header>
      {status}
      {toolbar}
      <div className="flex min-h-0 flex-1">
        {showBody ? (
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto">{children}</div>
        ) : null}
        {phone && phoneShowingNav ? (
          <div className="min-h-0 min-w-0 flex-1 overflow-y-auto" data-testid="settings-phone-nav">
            <SettingsNav page={page} onPage={onPage} groups={navGroups} />
          </div>
        ) : null}
        {showRail ? (
          <div className={tablet ? 'w-40 shrink-0' : 'w-56 shrink-0'} data-testid="settings-home-rail">
            <SettingsNav page={page} onPage={onPage} groups={navGroups} compact={tablet} edge="end" />
          </div>
        ) : null}
      </div>
    </aside>
  )
}
